import React, { useState, useEffect } from 'react';
import { stockMovementService } from '../../api/stockMovementService';
import StockMovementForm from '../StockMovements/StockMovementForm';
import Table from '../Common/Table';
import './ProductStockMovements.css';

const TYPE_LABELS = {
  in: 'Entrée',
  sale: 'Vente',
  gift: 'Cadeau',
  destruction: 'Destruction',
};

const TYPE_BADGES = {
  in: 'badge-success',
  sale: 'badge-info',
  gift: 'badge-warning',
  destruction: 'badge-danger',
};

function ProductStockMovements({ product, onChanged }) {
  const [movements, setMovements] = useState([]);
  const [stockQuantity, setStockQuantity] = useState(product?.stock_quantity ?? 0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const response = await stockMovementService.getStockMovements({
        product_id: product.id,
        per_page: 100,
      });
      setMovements(response.data || []);
    } catch (error) {
      console.error('Error fetching product movements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  // Recompute current stock from the loaded movements so the header stays in sync
  // after adding/cancelling a movement without needing the parent to refetch.
  useEffect(() => {
    const stockIn = movements
      .filter((m) => m.movement_type === 'in')
      .reduce((sum, m) => sum + parseInt(m.quantity, 10), 0);
    const stockOut = movements
      .filter((m) => m.movement_type !== 'in')
      .reduce((sum, m) => sum + parseInt(m.quantity, 10), 0);
    setStockQuantity(stockIn - stockOut);
  }, [movements]);

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchMovements();
    if (onChanged) onChanged();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler ce mouvement ? Cela inversera la modification du stock.')) {
      try {
        await stockMovementService.deleteStockMovement(id);
        fetchMovements();
        if (onChanged) onChanged();
      } catch (error) {
        console.error('Error cancelling movement:', error);
        alert('Échec de l\'annulation du mouvement');
      }
    }
  };

  const columns = [
    {
      key: 'movement_type',
      label: 'Type',
      render: (val) => <span className={`badge ${TYPE_BADGES[val]}`}>{TYPE_LABELS[val] || val}</span>,
    },
    {
      key: 'quantity',
      label: 'Quantité',
      render: (val, row) => (
        <span style={{ fontWeight: 600, color: row.movement_type === 'in' ? '#38a169' : '#e53e3e' }}>
          {row.movement_type === 'in' ? '+' : '-'}{val}
        </span>
      ),
    },
    { key: 'unit_price', label: 'Prix unitaire', render: (val) => `${parseFloat(val).toFixed(2)} DH` },
    {
      key: 'total_amount',
      label: 'Total',
      render: (val, row) => `${(parseFloat(row.quantity) * parseFloat(row.unit_price)).toFixed(2)} DH`,
    },
    { key: 'reference', label: 'Référence', render: (val) => val || '-' },
    { key: 'creator', label: 'Créé par', render: (val) => val?.name || 'N/A' },
    { key: 'created_at', label: 'Date', render: (val) => new Date(val).toLocaleDateString() },
  ];

  return (
    <div className="product-movements">
      <div className="product-movements-header">
        <div className="product-movements-stock">
          <span className="product-movements-stock-label">Stock actuel</span>
          <span className="product-movements-stock-value">{stockQuantity} unités</span>
        </div>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Ajouter un mouvement
          </button>
        )}
      </div>

      {showForm && (
        <div className="product-movements-form">
          <StockMovementForm
            presetProduct={{ ...product, stock_quantity: stockQuantity }}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      <h3 className="product-movements-title">Historique des mouvements</h3>
      <Table
        columns={columns}
        data={movements}
        loading={loading}
        onDelete={handleDelete}
        actions={true}
      />
    </div>
  );
}

export default ProductStockMovements;
